"use client"

import { motion } from "framer-motion"

const GRAIN_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>" +
      "<filter id='n'>" +
      "<feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>" +
      "<feColorMatrix type='saturate' values='0'/>" +
      "</filter>" +
      "<rect width='100%' height='100%' filter='url(#n)'/>" +
      "</svg>"
  )

const STARS = [
  { top: "11%", left: "14%", size: 2, delay: 0 },
  { top: "18%", left: "78%", size: 1.5, delay: 0.4 },
  { top: "28%", left: "62%", size: 2.5, delay: 0.8 },
  { top: "34%", left: "22%", size: 1.5, delay: 1.2 },
  { top: "52%", left: "88%", size: 2, delay: 0.6 },
  { top: "58%", left: "10%", size: 2, delay: 1.4 },
  { top: "72%", left: "72%", size: 1.5, delay: 0.2 },
  { top: "80%", left: "38%", size: 2, delay: 1.0 },
  { top: "86%", left: "56%", size: 1.5, delay: 1.6 },
] as const

type GenesisAuraVariant = "forming" | "locked" | "idle"

interface GenesisAuraBackgroundProps {
  variant?: GenesisAuraVariant
}

/** Abstract genesis field — no figure, no silhouette. Sparks curiosity while the character stays hidden. */
export function GenesisAuraBackground({ variant = "forming" }: GenesisAuraBackgroundProps) {
  const isLocked = variant === "locked"
  const isIdle = variant === "idle"

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#030304]" aria-hidden>
      {/* Deep vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 50% 42%, rgba(8,12,28,0.35) 0%, transparent 55%), radial-gradient(120% 100% at 50% 50%, transparent 40%, rgba(0,0,0,0.92) 100%)",
        }}
      />

      {/* Drifting color blooms */}
      <motion.div
        className="absolute left-1/2 top-[38%] h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[72px]"
        style={{
          background: isLocked
            ? "color-mix(in srgb, var(--primary) 38%, transparent)"
            : "color-mix(in srgb, var(--accent-blue) 42%, transparent)",
        }}
        animate={{ scale: [1, 1.18, 1], opacity: [0.45, 0.72, 0.45] }}
        transition={{ duration: isIdle ? 7 : 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute left-[28%] top-[52%] h-32 w-32 rounded-full blur-[56px]"
        style={{ background: "color-mix(in srgb, var(--primary) 22%, transparent)" }}
        animate={{ x: [0, 18, 0], y: [0, -12, 0], opacity: [0.25, 0.45, 0.25] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute right-[22%] top-[30%] h-28 w-28 rounded-full blur-[48px]"
        style={{ background: "color-mix(in srgb, #7c5cff 18%, transparent)" }}
        animate={{ x: [0, -14, 0], y: [0, 10, 0], opacity: [0.2, 0.38, 0.2] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />

      {/* Concentric portal rings — something is forming, but not a person */}
      <motion.div
        className="absolute left-1/2 top-[42%] size-[min(72vw,260px)] -translate-x-1/2 -translate-y-1/2"
        animate={{ rotate: 360 }}
        transition={{ duration: isIdle ? 90 : 55, repeat: Infinity, ease: "linear" }}
      >
        <svg viewBox="0 0 200 200" className="size-full">
          <circle cx="100" cy="100" r="88" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.75" />
          <circle
            cx="100"
            cy="100"
            r="68"
            fill="none"
            stroke="rgba(255,255,255,0.07)"
            strokeWidth="0.75"
            strokeDasharray="3 9"
          />
          <circle
            cx="100"
            cy="100"
            r="48"
            fill="none"
            stroke="color-mix(in srgb, var(--primary) 35%, transparent)"
            strokeWidth="1"
            strokeDasharray="6 14"
            strokeLinecap="round"
          />
        </svg>
      </motion.div>

      {/* Counter-rotating inner ring */}
      <motion.div
        className="absolute left-1/2 top-[42%] size-[min(52vw,180px)] -translate-x-1/2 -translate-y-1/2"
        animate={{ rotate: -360 }}
        transition={{ duration: isIdle ? 70 : 38, repeat: Infinity, ease: "linear" }}
      >
        <svg viewBox="0 0 200 200" className="size-full">
          <circle
            cx="100"
            cy="100"
            r="72"
            fill="none"
            stroke="color-mix(in srgb, var(--accent-blue) 28%, transparent)"
            strokeWidth="1.25"
            strokeDasharray="2 16"
            strokeLinecap="round"
          />
        </svg>
      </motion.div>

      {/* Genesis core — the light at the center */}
      <motion.div
        className="absolute left-1/2 top-[42%] size-16 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          background:
            "radial-gradient(circle, color-mix(in srgb, var(--primary) 75%, white) 0%, color-mix(in srgb, var(--primary) 30%, transparent) 45%, transparent 70%)",
          boxShadow: isLocked
            ? "0 0 48px color-mix(in srgb, var(--primary) 40%, transparent)"
            : "0 0 64px color-mix(in srgb, var(--accent-blue) 50%, transparent)",
        }}
        animate={{ scale: [0.92, 1.08, 0.92], opacity: [0.55, 0.9, 0.55] }}
        transition={{ duration: isLocked ? 3.5 : 2.8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Star field */}
      {STARS.map((star, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            top: star.top,
            left: star.left,
            width: star.size,
            height: star.size,
          }}
          animate={{ opacity: [0.15, 0.55, 0.15] }}
          transition={{ duration: 3 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: star.delay }}
        />
      ))}

      {/* Film grain */}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-soft-light"
        style={{ backgroundImage: `url("${GRAIN_SVG}")`, backgroundSize: "140px 140px" }}
      />

      {/* Bottom legibility fade for overlaid text */}
      <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
    </div>
  )
}
